// @ts-strict-ignore
import {Dashboard, Trackable} from 'common/report/dashboards/types';
import {DashboardCRUDSource, getCommonDashboardTrackingProps} from 'common/report/dashboards/tracking';
import {buildCreateEntityModalAttrs, normalizeDashboardEntity} from 'common/report/util/saved-entities';
import {EntityType} from 'common/report/util/saved-entities/types';
import {LocalizationStore} from 'common/i18n/store';

interface StagedDashboardOptions {
  isSaveAsNew?: boolean;
  crudSource?: DashboardCRUDSource;
}

interface InvalidStagedDashboardErrors {
  title?: boolean;
}

export class StagedDashboard implements Trackable {
  public declare dashboard: Partial<Dashboard>;
  public declare originalDashboard: Partial<Dashboard>;

  public loading = false;
  public declare isSaveAsNew: boolean;
  public declare duplicate: number;
  public invalid: InvalidStagedDashboardErrors = {};
  public declare crudSource: DashboardCRUDSource;

  constructor(dashboard: Partial<Dashboard>, options: StagedDashboardOptions = {}) {
    this.originalDashboard = {
      ...dashboard,
    };
    this.dashboard = dashboard;
    this.crudSource = options.crudSource;
    this.isSaveAsNew = options.isSaveAsNew;

    if (options.isSaveAsNew) {
      this.duplicate = this.dashboard.id;
      this.dashboard.id = null;
    }
  }

  public validate() {
    const invalid = {
      title: !(this.dashboard.title !== null && this.dashboard.title.length),
    };
    this.updateAttrs({invalid});
  }

  public isValid(): boolean {
    return !Object.values(this.invalid).find(Boolean);
  }

  // set calls

  public setLoading(loading: boolean) {
    return this.updateAttrs({loading});
  }

  public isCreating(): boolean {
    return !this.dashboard.id && !this.duplicate;
  }

  public isDuplicating(): boolean {
    return !!this.duplicate && !this.dashboard.id;
  }

  public isEditing(): boolean {
    return !!this.dashboard.id && !this.duplicate;
  }

  public serialize() {
    const dashboard = this.dashboard;
    return {
      ...dashboard,
      cards: dashboard.cards && dashboard.cards.length > 0 ? dashboard.cards.map((card) => ({...card})) : [],
      card_order: dashboard.card_order && dashboard.card_order.length > 0 ? [...dashboard.card_order] : [],
    };
  }

  public toTrackingData(): Obj {
    const trackingData = getCommonDashboardTrackingProps({
      dashboard: this.dashboard,
      sourceTrackingProps: {crudSource: this.crudSource},
    });

    if (!this.isCreating()) {
      trackingData[`changed description`] = this.dashboard.description !== this.originalDashboard.description;
      trackingData[`changed title`] = this.dashboard.title !== this.originalDashboard.title;
      trackingData[`dashboard id`] = this.dashboard.id;
    }
    return trackingData;
  }

  private updateAttrs(attrs) {
    Object.assign(this, attrs);
  }

  public updateDashboardAttrs(attrs) {
    this.dashboard = {
      ...this.dashboard,
      ...attrs,
    };
    return this;
  }

  public getCreateEntityModalAttrs({projectId, i18n}: {projectId: number; i18n: LocalizationStore}) {
    const isCreating = this.isCreating();
    const isDuplicating = this.isDuplicating();
    const isEditing = !(isCreating || isDuplicating);

    return buildCreateEntityModalAttrs({
      // casting to Dashboard because this.dashboard may be a partial
      // when creating or shallow-editing a dashboard
      entity: normalizeDashboardEntity(this.dashboard as Dashboard),
      entityType: EntityType.Dashboard,
      isDuplicating,
      isEditing,
      modalOpen: true,
      projectId,
      i18n,
    });
  }
}
